import React, { useState, useEffect } from 'react';
import Modal from '../modal';
import Toggle from '../toggle/toggle';
import Utils from '../../utils/utils';
import {
    REVIEW_MODE_CLIENT_NON_CLIENT,
    REVIEW_MODE_CLIENT_ONLY,
    REVIEW_MODE_CUSTOM,
} from './review-seed-presets';
import {
    FIELD_IMPUTATION_LEVEL1,
    RETIRED_FIELD_EXPENSE_CATEGORY,
    RETIRED_FIELD_IMPUTATION_SUBCATEGORY,
    RETIRED_FIELD_OPTIONS,
    REVIEW_FIELD_OPTION_ORDER,
    REQUIRED_TOGGLE_FIELD_OPTIONS,
} from './expense-field-flags';

const FIELD_IMPUTATION_SUBCATEGORY = RETIRED_FIELD_IMPUTATION_SUBCATEGORY;
const REVIEW_FIELD_ORDER_SET = new Set(REVIEW_FIELD_OPTION_ORDER);

const OptionRow = ({
    moduleId,
    option,
    index,
    isChecked,
    onToggle,
    data,
    showRequiredToggle = false,
    isRequired = false,
    onRequiredToggle,
    disabled = false,
}) => {
    const toggleId = `${moduleId}-${option.name}-${index}`;
    const requiredToggleId = `${toggleId}-required`;
    const requiredLabel = data?.t?.read('module_options.required') ?? 'Required';

    return (
        <div className="mb-3" data-testid={`module-option-${option.name}`}>
            <div className={showRequiredToggle ? "d-flex align-items-start justify-content-between" : ""}>
                <div className="flex-grow-1">
                    <Toggle
                        id={toggleId}
                        isOn={isChecked}
                        handleToggle={() => onToggle(option.name)}
                        label={data?.t?.read(`module_options.${option.name}`) ?? data?.t?.read(option.name) ?? option.name}
                        disabled={disabled}
                    />
                </div>
                {showRequiredToggle && isChecked && (
                    <div className="ml-4" data-testid={`module-option-required-${option.name}`}>
                        <Toggle
                            id={requiredToggleId}
                            isOn={isChecked && isRequired}
                            disabled={disabled || !isChecked}
                            handleToggle={() => onRequiredToggle?.(option.name, !isRequired)}
                            label={requiredLabel}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const ModuleOptionsModal = ({
    isOpen,
    moduleId,
    moduleLabel,
    selectedComponents,
    components,
    data,
    toggleModuleOption,
    addDefaultImputations,
    reviewSeedPromptAnswered,
    markReviewSeedPromptAnswered,
    requiredExpenseFields,
    setExpenseFieldRequired,
    reviewMode,
    setExpenseReviewMode,
    onClose
}) => {
    const [showImputationSeedModal, setShowImputationSeedModal] = useState(false);
    const [pendingPresetMode, setPendingPresetMode] = useState(null);

    useEffect(() => {
        if (!isOpen) {
            setShowImputationSeedModal(false);
            setPendingPresetMode(null);
        }
    }, [isOpen]);

    if (!isOpen || !moduleId) return null;

    const component = components.find(comp => comp._id === moduleId);
    const options = component?.options || [];
    const selected = selectedComponents.find(comp => comp.id === moduleId);

    const mainOptions = options.filter(opt => !opt.name.startsWith('field_'));
    const fieldOptions = options.filter(
        opt => opt.name.startsWith('field_') && !RETIRED_FIELD_OPTIONS.has(opt.name)
    );
    const isReviewActive = !!selected?.options?.some(opt => opt.name === 'review');
    const isExpensesModule = component?.name === 'expenses';
    const currentReviewMode = reviewMode || REVIEW_MODE_CUSTOM;
    const isCustomMode = currentReviewMode === REVIEW_MODE_CUSTOM;
    const templateOptionNames = options.map((option) => option.name);

    const hasSelectedOption = (optionName) => !!selected?.options?.some(opt => opt.name === optionName);
    const isChecked = (optionName) => {
        if (optionName === FIELD_IMPUTATION_LEVEL1) {
            return hasSelectedOption(FIELD_IMPUTATION_LEVEL1) || hasSelectedOption(RETIRED_FIELD_EXPENSE_CATEGORY);
        }
        return hasSelectedOption(optionName);
    };
    const selectedData = {
        config: {
            components: selected ? [{ ...selected, name: selected.name ?? component?.name }] : [],
            ...(Array.isArray(requiredExpenseFields)
                ? { expenses_review: { required_fields: requiredExpenseFields } }
                : {}),
        },
    };
    const isRequired = (optionName) => Utils.isExpenseFieldRequired(selectedData, optionName);
    const supportsRequiredToggle = (optionName) => REQUIRED_TOGGLE_FIELD_OPTIONS.has(optionName);
    const clearRequired = (optionName) => setExpenseFieldRequired?.(optionName, false);
    const modeOptions = [
        {
            value: REVIEW_MODE_CLIENT_ONLY,
            label: data?.t?.read('module_options.review_mode_client_only') ?? 'Client-only',
        },
        {
            value: REVIEW_MODE_CLIENT_NON_CLIENT,
            label: data?.t?.read('module_options.review_mode_client_non_client') ?? 'Client / Non-client',
        },
        {
            value: REVIEW_MODE_CUSTOM,
            label: data?.t?.read('module_options.review_mode_custom') ?? 'Custom',
        },
    ];

    const orderedFieldOptions = [
        ...REVIEW_FIELD_OPTION_ORDER.map((name) => fieldOptions.find((opt) => opt.name === name)).filter(
            Boolean
        ),
        ...fieldOptions.filter((opt) => !REVIEW_FIELD_ORDER_SET.has(opt.name)),
    ];

    const handleOptionToggle = (optionName) => {
        const wasChecked = isChecked(optionName);

        if (optionName === 'review') {
            toggleModuleOption?.(moduleId, 'review');
            if (!wasChecked && component?.name === 'expenses' && !reviewSeedPromptAnswered) {
                setShowImputationSeedModal(true);
            }
            return;
        }

        if (wasChecked) {
            if (optionName === FIELD_IMPUTATION_LEVEL1 && isChecked(FIELD_IMPUTATION_SUBCATEGORY)) {
                toggleModuleOption?.(moduleId, FIELD_IMPUTATION_SUBCATEGORY);
                clearRequired(FIELD_IMPUTATION_SUBCATEGORY);
            }
            if (optionName === 'field_matter' && isChecked('field_matter_split')) {
                toggleModuleOption?.(moduleId, 'field_matter_split');
                clearRequired('field_matter_split');
            }
            clearRequired(optionName);
            if (
                optionName === FIELD_IMPUTATION_LEVEL1 &&
                hasSelectedOption(RETIRED_FIELD_EXPENSE_CATEGORY)
            ) {
                toggleModuleOption?.(moduleId, RETIRED_FIELD_EXPENSE_CATEGORY);
                clearRequired(RETIRED_FIELD_EXPENSE_CATEGORY);
                if (!hasSelectedOption(FIELD_IMPUTATION_LEVEL1)) {
                    return;
                }
            }
        } else if (
            optionName === FIELD_IMPUTATION_SUBCATEGORY &&
            !isChecked(FIELD_IMPUTATION_LEVEL1)
        ) {
            toggleModuleOption?.(moduleId, FIELD_IMPUTATION_LEVEL1);
        } else if (optionName === 'field_matter_split' && !isChecked('field_matter')) {
            toggleModuleOption?.(moduleId, 'field_matter');
        }

        toggleModuleOption?.(moduleId, optionName);
    };

    const handleSeedConfirm = () => {
        addDefaultImputations?.();
        markReviewSeedPromptAnswered?.();
        setShowImputationSeedModal(false);
    };

    const handleSeedSkip = () => {
        markReviewSeedPromptAnswered?.();
        setShowImputationSeedModal(false);
    };

    const handleReviewModeToggle = (mode) => {
        if (mode === currentReviewMode) {
            return;
        }
        const switchingAwayFromCustom = isCustomMode && mode !== REVIEW_MODE_CUSTOM;
        const hasCustomFields = fieldOptions.some(opt => isChecked(opt.name));
        if (switchingAwayFromCustom && hasCustomFields) {
            setPendingPresetMode(mode);
            return;
        }
        setExpenseReviewMode?.(mode, moduleId, templateOptionNames);
    };

    const handlePresetConfirm = () => {
        setExpenseReviewMode?.(pendingPresetMode, moduleId, templateOptionNames);
        setPendingPresetMode(null);
    };

    const handlePresetCancel = () => {
        setPendingPresetMode(null);
    };

    const handleClose = () => {
        if (pendingPresetMode) {
            return;
        }
        onClose?.();
    };

    return (
        <>
        <Modal
            title={`${data?.t?.read('module_options.configure_title_prefix') ?? 'Configure'} ${moduleLabel || 'Module'} ${data?.t?.read('module_options.configure_title_suffix') ?? 'Options'}`}
            okText={data?.t?.read('close')}
            okAction={handleClose}
            onClose={handleClose}
        >
            <div className="module-options-modal">
                {mainOptions.length ? (
                    mainOptions.map((option, i) => (
                        <OptionRow
                            key={option.name}
                            moduleId={moduleId}
                            option={option}
                            index={i}
                            isChecked={isChecked(option.name)}
                            onToggle={handleOptionToggle}
                            data={data}
                        />
                    ))
                ) : (
                    <p className="text-muted mb-0">No options available for this module.</p>
                )}

                {isReviewActive && isExpensesModule && (
                    <div className="mt-4 pt-1" data-testid="review-mode-section">
                        <p className="font-weight-bold mb-2">
                            {data?.t?.read('module_options.review_mode') ?? 'Review mode'}
                        </p>
                        {modeOptions.map((mode) => (
                            <div className="mb-3" key={mode.value} data-testid={`review-mode-${mode.value}`}>
                                <Toggle
                                    id={`${moduleId}-review-mode-${mode.value}`}
                                    isOn={currentReviewMode === mode.value}
                                    handleToggle={() => handleReviewModeToggle(mode.value)}
                                    label={mode.label}
                                />
                            </div>
                        ))}
                        {!isCustomMode && (
                            <p className="text-muted small mb-0">
                                {data?.t?.read('module_options.review_mode_preset_help') ??
                                    'This mode applies a fixed review form preset. Select Custom to edit fields.'}
                            </p>
                        )}
                    </div>
                )}


                {isReviewActive && isCustomMode && fieldOptions.length > 0 && (
                    <div className="mt-4 pt-1" data-testid="field-flags-section">
                        <p className="font-weight-bold mb-2">
                            {data?.t?.read('module_options.review_fields') ?? 'Review form fields'}
                        </p>
                        {orderedFieldOptions.map((option, i) => {
                            if (option.name === 'field_matter_split' && !isChecked('field_matter')) {
                                return null;
                            }
                            const isSubOption = option.name === 'field_matter_split';
                            return (
                                <div key={option.name} className={isSubOption ? 'pl-3 border-left' : ''}>
                                    <OptionRow
                                        moduleId={moduleId}
                                        option={option}
                                        index={i}
                                        isChecked={isChecked(option.name)}
                                        onToggle={handleOptionToggle}
                                        data={data}
                                        showRequiredToggle={supportsRequiredToggle(option.name)}
                                        isRequired={isRequired(option.name)}
                                        onRequiredToggle={setExpenseFieldRequired}
                                        disabled={isExpensesModule && !isCustomMode}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </Modal>

        {showImputationSeedModal && (
            <Modal
                title={data?.t?.read('module_options.review_seed_title')}
                disableBackdropClose
                onClose={() => setShowImputationSeedModal(false)}
            >
                <div data-testid="review-seed-modal">
                    <p className="mb-3">
                        {data?.t?.read('module_options.review_seed_intro')}
                    </p>
                    <div className="d-flex justify-content-end" style={{ gap: '8px' }}>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleSeedSkip}
                        >
                            {data?.t?.read('module_options.review_seed_skip')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSeedConfirm}
                        >
                            {data?.t?.read('module_options.review_seed_confirm')}
                        </button>
                    </div>
                </div>
            </Modal>
        )}

        {pendingPresetMode && (
            <Modal
                title={data?.t?.read('module_options.review_mode_discard_title')}
                disableBackdropClose
                onClose={handlePresetCancel}
            >
                <div data-testid="review-mode-discard-modal">
                    <p className="mb-3">
                        {data?.t?.read('module_options.review_mode_discard_body')}
                    </p>
                    <div className="d-flex justify-content-end" style={{ gap: '8px' }}>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handlePresetCancel}
                        >
                            {data?.t?.read('module_options.review_mode_discard_cancel')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handlePresetConfirm}
                        >
                            {data?.t?.read('module_options.review_mode_discard_confirm')}
                        </button>
                    </div>
                </div>
            </Modal>
        )}
        </>
    );
};

export default ModuleOptionsModal;
